
import { computed, ComputedRef, defineComponent, onMounted, reactive, ref, watch } from 'vue';
import Layout from '@/components/Layout.vue';
import { useRoute } from 'vue-router';
import SidebarList from '@/components/SidebarList.vue';
import SidebarListItem from '@/components/SidebarListItem.vue';
import Modal from '@/components/Modal.vue';

import { IClaimBatchItem, IClaimMedia, QueryRecord } from '@/types';
import { useBatch } from '@/hooks';

export default defineComponent({
  name: 'Gallery',
  components: {
    Layout,
    SidebarList,
    SidebarListItem,
    Modal,
  },
  setup() {
    const route = useRoute();
    const batchId = route.params.id.toString();
    const claimIdProp = Number(route.query.claim) || null;
    const {
      loadingBatchMedia,
      batchMediaData: _unsortedBatchMediaData,
      batchMediaError,
      getBatchMedia,
      getBatchMediaDownloadLink,
    }: {
      loadingBatchMedia: ComputedRef<boolean>;
      batchMediaData: ComputedRef<IClaimBatchItem[] | undefined>;
      batchMediaError: ComputedRef<string | null>;
      getBatchMedia: (query?: QueryRecord | undefined) => Promise<void>;
      getBatchMediaDownloadLink: () => string;
    } = useBatch(batchId);

    const showModal = ref<boolean>(false);
    const currentClaimId = ref<number | null>(claimIdProp);

    const sortedBatchMediaData = computed<IClaimBatchItem[] | undefined>(() => {
      const temp: IClaimBatchItem[] = _unsortedBatchMediaData.value || [];
      return temp.sort((a, b) => a.claimId - b.claimId);
    });

    const _currentBatchMediaItem = computed<IClaimBatchItem | undefined>(() => {
      const temp: IClaimBatchItem[] = _unsortedBatchMediaData.value || [];
      return temp.find((batchItem: IClaimBatchItem) => batchItem.claimId === currentClaimId.value);
    });

    const currentClaimMedia = computed<IClaimMedia[]>(() => {
      return _currentBatchMediaItem.value?.media || [];
    });

    const currentMedia = ref<IClaimMedia | null>(null);

    const toggleModal = () => {
      showModal.value = !showModal.value;
    };

    const onSelectClaim = (claimId: number) => {
      currentClaimId.value = claimId;
    };

    const onSelectMedia = (media: IClaimMedia) => {
      toggleModal();
      currentMedia.value = media;
    };

    const _sidebarClaimRefs: { [key: string]: HTMLElement } = reactive({});

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const setSidebarClaimRef = (elementRef: any, claimId: number) => {
      if (elementRef && claimId) {
        _sidebarClaimRefs[claimId] = elementRef.$el;
      }
    };

    // default currentClaimId to the first item in the list unless we have something valid selected already
    watch(sortedBatchMediaData, () => {
      if (sortedBatchMediaData.value?.length) {
        if (
          !currentClaimId.value ||
          !sortedBatchMediaData.value.some(
            (item: IClaimBatchItem) => item.claimId === currentClaimId.value
          )
        ) {
          currentClaimId.value = sortedBatchMediaData.value[0].claimId;
        }
      }
    });

    if (claimIdProp) {
      const stopper = watch(
        () => _sidebarClaimRefs[claimIdProp],
        (element: HTMLElement) => {
          element.scrollIntoView();
          stopper();
        }
      );
    }

    onMounted(() => {
      getBatchMedia();
    });

    return {
      batchId,
      setSidebarClaimRef,
      showModal,
      toggleModal,
      onSelectClaim,
      onSelectMedia,
      loadingBatchMedia,
      sortedBatchMediaData,
      batchMediaError,
      currentClaimId,
      currentClaimMedia,
      currentMedia,
      getBatchMediaDownloadLink,
    };
  },
});
